import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { DomainTopicsService, ParentTopic, TopicLink, TopicGraphNode, TopicGraphEdge, TopicTreeResponse, TopicTreeNode } from '../../services/domain-topics';
import { FeatureFlagService } from '../../services/feature-flag';
import { TopicDiagnosticComponent } from '../topic-diagnostic/topic-diagnostic';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-domain-topics',
  standalone: true,
  imports: [CommonModule, FormsModule, TopicDiagnosticComponent],
  templateUrl: './domain-topics.html',
  styleUrl: './domain-topics.css'
})
export class DomainTopicsComponent implements OnInit {
  domain: string = '';
  topics: ParentTopic[] = [];
  selectedTopic: ParentTopic | null = null;
  loading = false;
  error: string | null = null;
  topicLinks: TopicLink[] = [];
  structuralLinks: TopicLink[] = [];
  loadingLinks = false;
  showLinks = false;
  
  // Graph visualization
  viewMode: 'list' | 'graph' = 'list';
  graphNodes: TopicGraphNode[] = [];
  graphEdges: TopicGraphEdge[] = [];
  loadingGraph = false;
  graphError: string | null = null;
  minSimilarity = 0.65;
  
  // Side panel for topic tree
  selectedTopicForTree: TopicGraphNode | null = null;
  topicTree: TopicTreeResponse | null = null;
  loadingTree = false;
  treeError: string | null = null;
  showSidePanel = false;

  showDiagnostic = false;
  diagnosticTopic: ParentTopic | null = null;

  // Share link
  private readonly API_URL = environment.apiUrl + '/v1';
  shareLoading = false;
  shareUrl: string | null = null;
  shareCopied = false;
  shareError: string | null = null;

  constructor(
    private domainTopicsService: DomainTopicsService,
    private http: HttpClient,
    public featureFlags: FeatureFlagService
  ) {}

  ngOnInit() {
    // Auto-load marker.io as example
    this.domain = 'marker.io';
    this.loadTopics();
  }

  loadGraph() {
    if (!this.domain.trim()) {
      this.graphError = 'Please enter a domain';
      return;
    }

    this.loadingGraph = true;
    this.graphError = null;
    this.graphNodes = [];
    this.graphEdges = [];

    this.domainTopicsService.getTopicGraph(this.domain, this.minSimilarity).subscribe({
      next: (response) => {
        this.loadingGraph = false;
        if (response.success && response.data) {
          this.graphNodes = response.data.nodes || [];
          this.graphEdges = response.data.edges || [];
          
          // Debug: Check if paths are in the data
          const nodesWithPaths = this.graphNodes.filter(n => n.pathToGrandparent && n.pathToGrandparent.length > 0);
          console.log(`📊 Graph loaded: ${this.graphNodes.length} nodes, ${this.graphEdges.length} edges`);
          console.log(`📊 Nodes with transitive paths: ${nodesWithPaths.length}`);
          if (nodesWithPaths.length > 0) {
            console.log('📊 Example paths:', nodesWithPaths.slice(0, 3).map(n => ({
              topic: n.id,
              grandparent: n.grandparentTopic,
              path: n.pathToGrandparent
            })));
          }
          
          // Check specifically for user-acceptance-testing
          const uatNode = this.graphNodes.find(n => n.id === 'user-acceptance-testing');
          if (uatNode) {
            console.log('📊 user-acceptance-testing node:', {
              id: uatNode.id,
              grandparent: uatNode.grandparentTopic,
              pathToGrandparent: uatNode.pathToGrandparent,
              hasPath: !!(uatNode.pathToGrandparent && uatNode.pathToGrandparent.length > 0)
            });
          }
          
          if (this.graphNodes.length === 0) {
            this.graphError = 'No topics found for this domain. Run aggregation first.';
          }
        } else {
          this.graphError = 'Failed to load topic graph';
        }
      },
      error: (err) => {
        this.loadingGraph = false;
        this.graphError = err.error?.message || err.error?.error || 'Failed to load topic graph. Make sure topics are aggregated first.';
        console.error('Error loading graph:', err);
      }
    });
  }

  switchView(mode: 'list' | 'graph') {
    this.viewMode = mode;
    if (mode === 'graph' && this.graphNodes.length === 0 && !this.loadingGraph) {
      this.loadGraph();
    }
  }

  getEdgeColor(edge: TopicGraphEdge): string {
    if (edge.type === 'merged') return '#4CAF50'; // Green
    if (edge.type === 'related') return '#FF9800'; // Orange
    return '#9E9E9E'; // Gray
  }

  getNodeColor(node: TopicGraphNode): string {
    if (node.authorityScore >= 70) return '#4CAF50'; // Green
    if (node.authorityScore >= 40) return '#FF9800'; // Orange
    return '#9E9E9E'; // Gray
  }

  getGrandparentForEdge(edge: TopicGraphEdge): string | null {
    // Find the grandparent topic for this edge
    // If both topics are in the same grandparentTopic, return that
    const sourceNode = this.graphNodes.find(n => n.id === edge.source);
    const targetNode = this.graphNodes.find(n => n.id === edge.target);
    
    if (!sourceNode || !targetNode) return null;
    
    // If they share a grandparent, return that
    if (sourceNode.grandparentTopic && targetNode.grandparentTopic) {
      if (sourceNode.grandparentTopic === targetNode.grandparentTopic) {
        return sourceNode.grandparentTopic;
      }
    }
    
    // If edge type is 'merged', check which one is the grandparent
    if (edge.type === 'merged') {
      // If source's grandparent is itself, source is the grandparent
      if (sourceNode.grandparentTopic === edge.source) {
        return edge.source;
      }
      // If target's grandparent is itself, target is the grandparent
      if (targetNode.grandparentTopic === edge.target) {
        return edge.target;
      }
      // If they share a grandparent, return that
      if (sourceNode.grandparentTopic && targetNode.grandparentTopic && 
          sourceNode.grandparentTopic === targetNode.grandparentTopic) {
        return sourceNode.grandparentTopic;
      }
    }
    
    return null;
  }

  loadTopics() {
    if (!this.domain.trim()) {
      this.error = 'Please enter a domain';
      return;
    }

    this.loading = true;
    this.error = null;
    this.selectedTopic = null;

    this.domainTopicsService.getDomainTopics(this.domain).subscribe({
      next: (response) => {
        this.loading = false;
        if (response.success && response.data.parentTopics) {
          this.topics = response.data.parentTopics;
          if (this.topics.length === 0) {
            this.error = response.data.message || 'No topics found for this domain. Run aggregation first.';
          }
        } else {
          this.error = response.data.message || 'Failed to load topics';
        }
      },
      error: (err) => {
        this.loading = false;
        this.error = err.error?.message || err.message || 'Failed to load topics';
        console.error('Error loading topics:', err);
      }
    });
  }

  selectTopic(topic: ParentTopic) {
    if (this.selectedTopic === topic) {
      this.selectedTopic = null;
      this.showLinks = false;
      this.topicLinks = [];
      this.structuralLinks = [];
    } else {
      this.selectedTopic = topic;
      this.showLinks = false;
      this.topicLinks = [];
      this.structuralLinks = [];
    }
  }

  getScoreColor(score: number): string {
    if (score >= 80) return '#10b981'; // green
    if (score >= 60) return '#3b82f6'; // blue
    if (score >= 40) return '#f59e0b'; // orange
    return '#ef4444'; // red
  }

  /**
   * Get tier badge styling
   */
  getTierBadgeClass(tier?: string): string {
    switch (tier) {
      case 'owned': return 'tier-badge tier-owned';
      case 'aspirational': return 'tier-badge tier-aspirational';
      case 'ambient': return 'tier-badge tier-ambient';
      case 'adjacent': return 'tier-badge tier-adjacent';
      case 'anti': return 'tier-badge tier-anti';
      default: return 'tier-badge tier-unclassified';
    }
  }

  /**
   * Get tier label
   */
  getTierLabel(tier?: string): string {
    switch (tier) {
      case 'owned': return '🏆 Owned';
      case 'aspirational': return '📈 Aspirational';
      case 'ambient': return '💬 Ambient';
      case 'adjacent': return '🔗 Adjacent';
      case 'anti': return '🚫 Filtered';
      default: return '❓ Unclassified';
    }
  }

  /**
   * Get tier description
   */
  getTierDescription(tier?: string): string {
    switch (tier) {
      case 'owned': return 'Strategic core topic - high authority, coverage, and focus';
      case 'aspirational': return 'Growing topic with good per-page quality';
      case 'ambient': return 'Mentioned frequently but not strategic (broad but shallow)';
      case 'adjacent': return 'Supporting topic, exists but not a strategic target';
      case 'anti': return 'Filtered out (brand mention or link-inflated)';
      default: return 'Does not match any classification tier';
    }
  }

  getScoreBarWidth(score: number): string {
    return `${Math.min(score, 100)}%`;
  }

  /**
   * Check if topic has boilerplate content penalty applied
   */
  hasBoilerplatePenalty(topic: ParentTopic): boolean {
    if (!topic.effectivePageCountForCoverage) {
      return false;
    }
    // If effective count is significantly less than actual count, penalty was applied
    return topic.effectivePageCountForCoverage < topic.pageCount * 0.5;
  }

  /**
   * Get the effective page count to display (or actual count if no penalty)
   */
  getDisplayPageCount(topic: ParentTopic): number {
    if (this.hasBoilerplatePenalty(topic) && topic.effectivePageCountForCoverage) {
      return Math.round(topic.effectivePageCountForCoverage);
    }
    return topic.pageCount;
  }

  /**
   * Get tooltip text explaining boilerplate penalty
   */
  getBoilerplateTooltip(topic: ParentTopic): string {
    if (!this.hasBoilerplatePenalty(topic) || !topic.effectivePageCountForCoverage) {
      return '';
    }
    const boilerplatePages = topic.pageCount - Math.round(topic.effectivePageCountForCoverage);
    return `This topic appears on ${topic.pageCount} pages, but ${boilerplatePages} pages contain boilerplate content (repeated FAQ blocks, static text). The effective coverage is ${Math.round(topic.effectivePageCountForCoverage)} pages after discounting boilerplate content.`;
  }

  /**
   * Check if topic has link score data
   * Phase 2: Internal Links
   */
  hasLinkScore(topic: ParentTopic): boolean {
    return topic.linkScore !== undefined && topic.linkScore > 0;
  }

  /**
   * Get link explanation text
   * Phase 2: Internal Links
   */
  getLinkExplanation(topic: ParentTopic): string {
    if (!topic.authorityExplanation?.links) {
      return '';
    }
    return topic.authorityExplanation.links;
  }

  /**
   * Load link details for selected topic
   * Phase 2: Internal Links
   */
  loadTopicLinks() {
    if (!this.selectedTopic || !this.domain) {
      return;
    }

    this.loadingLinks = true;
    this.showLinks = true;
    this.topicLinks = [];
    this.structuralLinks = [];

    this.domainTopicsService.getTopicLinks(this.domain, this.selectedTopic.parentTopicSlug).subscribe({
      next: (response) => {
        this.loadingLinks = false;
        if (response.success && response.data) {
          this.topicLinks = response.data.contextualLinks || [];
          this.structuralLinks = response.data.structuralLinks || [];
        }
      },
      error: (err) => {
        this.loadingLinks = false;
        console.error('Error loading topic links:', err);
      }
    });
  }

  /**
   * Toggle link details visibility
   */
  toggleLinks() {
    if (!this.showLinks && this.selectedTopic) {
      this.loadTopicLinks();
    } else {
      this.showLinks = false;
    }
  }
  
  openTopicTree(node: TopicGraphNode) {
    this.selectedTopicForTree = node;
    this.showSidePanel = true;
    this.loadingTree = true;
    this.treeError = null;
    this.topicTree = null;
    
    this.domainTopicsService.getTopicTree(this.domain, node.id, 3, 5).subscribe({
      next: (response) => {
        this.loadingTree = false;
        if (response.success && response.data) {
          this.topicTree = response;
        } else {
          this.treeError = 'Failed to load topic tree';
        }
      },
      error: (err) => {
        this.loadingTree = false;
        this.treeError = err.error?.message || err.error?.error || 'Failed to load topic tree';
        console.error('Error loading topic tree:', err);
      }
    });
  }
  
  closeSidePanel() {
    this.showSidePanel = false;
    this.selectedTopicForTree = null;
    this.topicTree = null;
  }
  
  getRelationshipColor(type?: string): string {
    switch (type) {
      case 'merged': return '#4CAF50'; // Green
      case 'related': return '#2196F3'; // Blue
      case 'somewhat-related': return '#FF9800'; // Orange
      case 'grandparent': return '#9C27B0'; // Purple
      case 'bridge': return '#00BCD4'; // Cyan
      default: return '#9E9E9E'; // Gray
    }
  }
  
  getRelationshipLabel(type?: string): string {
    switch (type) {
      case 'merged': return 'Merged';
      case 'related': return 'Related';
      case 'somewhat-related': return 'Somewhat Related';
      case 'grandparent': return 'Grandparent';
      case 'bridge': return 'Bridge';
      default: return 'Unknown';
    }
  }

  /**
   * Get filtered topics for User View
   * Filters topics with authority > 30 AND tier in ['owned', 'aspirational', 'adjacent']
   * Limits to top 2-3 topics
   */
  get filteredTopics(): ParentTopic[] {
    if (!this.featureFlags.isUserView()) {
      return this.topics;
    }
    
    // User View: Filter by authority > 30 AND tier in owned/aspirational/adjacent
    const filtered = this.topics.filter(topic => {
      const hasAuthority = topic.authorityScore > 30;
      const hasValidTier = topic.topicTier && ['owned', 'aspirational', 'adjacent'].includes(topic.topicTier);
      return hasAuthority && hasValidTier;
    });
    
    // Sort by authority score (descending) and limit to top 3
    return filtered.sort((a, b) => b.authorityScore - a.authorityScore).slice(0, 3);
  }

  /**
   * Open diagnostic view for a specific topic
   */
  openDiagnostic(topic: ParentTopic) {
    this.diagnosticTopic = topic;
    this.showDiagnostic = true;
  }

  /**
   * Close diagnostic view
   */
  closeDiagnostic() {
    this.showDiagnostic = false;
    this.diagnosticTopic = null;
  }

  // ─── Share Link ──────────────────────────────────────────────

  /**
   * Create a public share link for the current domain overview
   */
  createShareLink() {
    if (!this.domain.trim() || this.shareLoading) return;

    this.shareLoading = true;
    this.shareUrl = null;
    this.shareCopied = false;
    this.shareError = null;

    this.http.post<{ success: boolean; data: { token: string } }>(`${this.API_URL}/share`, {
      resourceType: 'domain-overview',
      resourceId: this.domain.trim(),
      label: `Topic Authority — ${this.domain}`,
    }).subscribe({
      next: (res) => {
        this.shareLoading = false;
        if (res.success && res.data?.token) {
          this.shareUrl = `${window.location.origin}/share/${res.data.token}`;
        } else {
          this.shareError = 'Failed to create share link';
        }
      },
      error: (err) => {
        this.shareLoading = false;
        this.shareError = err.error?.message || 'Failed to create share link';
      },
    });
  }

  /**
   * Copy the share URL to the clipboard
   */
  copyShareUrl() {
    if (!this.shareUrl) return;
    navigator.clipboard.writeText(this.shareUrl).then(() => {
      this.shareCopied = true;
      setTimeout(() => (this.shareCopied = false), 2000);
    });
  }

  /**
   * Dismiss the share link inline UI
   */
  dismissShare() {
    this.shareUrl = null;
    this.shareError = null;
    this.shareCopied = false;
  }
}

